import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Typography,
  Container,
  Grid,
  Card,
  CardContent,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Tooltip,
  IconButton,
  Collapse,
  Link,
  Button,
  Badge
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Warning as WarningIcon,
  Lightbulb as LightbulbIcon,
  CheckCircle as CheckCircleIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Launch as LaunchIcon,
  ContentCopy as ContentCopyIcon,
  Visibility as VisibilityIcon,
  Speed as SpeedIcon,
  EmojiEvents as TrophyIcon
} from '@mui/icons-material';
import { RootState } from '../../store';
import WebsiteSelector from '../../components/common/WebsiteSelector';
import { 
  competitorService, 
  CompetitorOverview, 
  KeywordCompetition, 
  CompetitorInsight,
  WebsiteCompetitor 
} from '../../services/competitorService';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`competitor-tabpanel-${index}`}
      aria-labelledby={`competitor-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const CompetitorsPage: React.FC = () => {
  const selectedWebsite = useSelector((state: RootState) => state.website.selectedWebsite);
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Data states
  const [competitorOverview, setCompetitorOverview] = useState<CompetitorOverview[]>([]);
  const [keywordCompetition, setKeywordCompetition] = useState<KeywordCompetition[]>([]);
  const [insights, setInsights] = useState<CompetitorInsight[]>([]);
  const [expandedKeywords, setExpandedKeywords] = useState<Set<string>>(new Set());
  const [websiteCompetitors, setWebsiteCompetitors] = useState<WebsiteCompetitor[]>([]);
  
  // Fetch data when website changes
  useEffect(() => {
    console.log('🌐 Selected website in CompetitorsPage:', selectedWebsite);
    if (selectedWebsite) {
      fetchCompetitorData();
    } else {
      console.log('⚠️ No website selected');
    }
  }, [selectedWebsite]);

  const fetchCompetitorData = async () => {
    if (!selectedWebsite) return;

    console.log('📊 Fetching competitor data for:', selectedWebsite.website_id);
    setLoading(true);
    setError(null);

    try {
      // Fetch all competitor data in parallel
      const [overviewResult, competitionResult, insightsResult, websiteCompResult] = await Promise.all([
        competitorService.getCompetitorOverview(selectedWebsite.website_id),
        competitorService.getKeywordCompetition(selectedWebsite.website_id),
        competitorService.generateInsights(selectedWebsite.website_id),
        competitorService.getWebsiteCompetitors(selectedWebsite.website_id)
      ]);

      if (overviewResult.error) {
        setError(overviewResult.error);
      } else {
        setCompetitorOverview(overviewResult.overview);
      }

      if (competitionResult.error) {
        console.error('Error fetching competition:', competitionResult.error);
      } else {
        setKeywordCompetition(competitionResult.competition);
      }

      if (insightsResult.error) {
        console.error('Error generating insights:', insightsResult.error);
      } else {
        setInsights(insightsResult.insights);
      }

      if (websiteCompResult.error) {
        console.error('Error fetching website competitors:', websiteCompResult.error);
      } else {
        setWebsiteCompetitors(websiteCompResult.competitors);
        console.log('Website competitors:', websiteCompResult.competitors);
      }
    } catch (err) {
      setError('Failed to load competitor data');
      console.error('Error fetching competitor data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const toggleKeywordExpansion = (keyword: string) => {
    const newExpanded = new Set(expandedKeywords);
    if (newExpanded.has(keyword)) {
      newExpanded.delete(keyword);
    } else {
      newExpanded.add(keyword);
    }
    setExpandedKeywords(newExpanded);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const getInsightIcon = (type: string) => {
    switch (type) {
      case 'opportunity':
        return <LightbulbIcon color="primary" />;
      case 'threat':
        return <WarningIcon color="error" />;
      case 'action':
        return <CheckCircleIcon color="success" />;
      default:
        return <LightbulbIcon />;
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'error';
      case 'medium':
        return 'warning';
      case 'low':
        return 'info';
      default:
        return 'default';
    }
  };

  // Check if a competitor domain is an official website (priority = 999)
  const isOfficialWebsite = (domain: string): boolean => {
    return websiteCompetitors.some(comp => 
      comp.competitor_domain === domain && comp.priority === 999
    );
  };

  return (
    <Container maxWidth="xl">
      <Box sx={{ py: 3 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h4" component="h1" gutterBottom>
              Competitor Analysis
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Monitor competitor performance and identify opportunities to improve your rankings
            </Typography>
          </Box>
          <WebsiteSelector showAddButton={false} />
        </Box>

        {!selectedWebsite ? (
          <Alert severity="info">
            Please select a website to view competitor analysis
          </Alert>
        ) : loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : (
          <>
            {/* Insights Section */}
            {insights.length > 0 && (
              <Box sx={{ mb: 4 }}>
                <Typography variant="h5" gutterBottom>
                  Key Insights & Recommendations
                </Typography>
                <Grid container spacing={2}>
                  {insights.slice(0, 4).map((insight, index) => (
                    <Grid item xs={12} sm={6} md={3} key={index}>
                      <Card sx={{ height: '100%' }}>
                        <CardContent>
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                            {getInsightIcon(insight.type)}
                            <Chip
                              label={insight.priority}
                              color={getPriorityColor(insight.priority) as any}
                              size="small"
                              sx={{ ml: 'auto' }}
                            />
                          </Box>
                          <Typography variant="subtitle2" gutterBottom>
                            {insight.title}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            {insight.description}
                          </Typography>
                          {insight.keywords && insight.keywords.length > 0 && (
                            <Box sx={{ mt: 1 }}>
                              <Typography variant="caption" color="text.secondary">
                                Example keywords:
                              </Typography>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                                {Array.from(new Set(insight.keywords)).slice(0, 3).map((keyword, idx) => (
                                  <Chip key={`${insight.title}-${keyword}-${idx}`} label={keyword} size="small" variant="outlined" />
                                ))}
                              </Box>
                            </Box>
                          )}
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}

            {/* Tabs */}
            <Paper sx={{ mb: 3 }}>
              <Tabs value={tabValue} onChange={handleTabChange}>
                <Tab label="Competitor Overview" />
                <Tab label="Keyword Competition" />
                <Tab label="Detailed Analysis" />
              </Tabs>
            </Paper>

            {/* Tab 1: Competitor Overview */}
            <TabPanel value={tabValue} index={0}>
              <Grid container spacing={3}>
                {competitorOverview.map((competitor) => {
                  const isOfficial = isOfficialWebsite(competitor.domain);
                  
                  return (
                    <Grid item xs={12} sm={6} md={4} key={competitor.domain}>
                      <Card 
                        sx={{ 
                          bgcolor: isOfficial ? 'grey.100' : 'background.paper',
                          opacity: isOfficial ? 0.85 : 1,
                          position: 'relative',
                          border: isOfficial ? '1px solid' : 'none',
                          borderColor: isOfficial ? 'grey.300' : 'transparent'
                        }}
                      >
                        {isOfficial && (
                          <Chip
                            label="Official Website"
                            size="small"
                            color="default"
                            sx={{
                              position: 'absolute',
                              top: 8,
                              right: 8,
                              backgroundColor: 'grey.700',
                              color: 'white',
                              fontSize: '0.7rem'
                            }}
                          />
                        )}
                        <CardContent>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
                              {competitor.name}
                            </Typography>
                            <IconButton
                              size="small"
                              onClick={() => window.open(`https://${competitor.domain}`, '_blank')}
                            >
                              <LaunchIcon fontSize="small" />
                            </IconButton>
                          </Box>
                          
                          <Typography variant="caption" color="text.secondary" gutterBottom>
                            {competitor.domain}
                          </Typography>

                        <Box sx={{ mt: 2 }}>
                          <Grid container spacing={2}>
                            <Grid item xs={6}>
                              <Box sx={{ textAlign: 'center' }}>
                                <Typography variant="h4" color="primary">
                                  {competitor.totalKeywords}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  Keywords
                                </Typography>
                              </Box>
                            </Grid>
                            <Grid item xs={6}>
                              <Box sx={{ textAlign: 'center' }}>
                                <Typography variant="h4" color="secondary">
                                  {competitor.avgPosition.toFixed(1)}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  Avg Position
                                </Typography>
                              </Box>
                            </Grid>
                          </Grid>

                          <Box sx={{ mt: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                              <TrophyIcon sx={{ fontSize: 16, mr: 1, color: 'warning.main' }} />
                              <Typography variant="body2">
                                {competitor.topPositions} top 3 positions
                              </Typography>
                            </Box>
                            
                            <Box sx={{ mt: 1 }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                <Typography variant="caption">Visibility</Typography>
                                <Typography variant="caption">{competitor.visibility.toFixed(1)}%</Typography>
                              </Box>
                              <LinearProgress
                                variant="determinate"
                                value={competitor.visibility}
                                sx={{ height: 6, borderRadius: 3 }}
                              />
                            </Box>
                          </Box>
                          
                          {isOfficial && (
                            <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                              <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                Official websites typically can't be outranked for branded searches
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                  );
                })}
              </Grid>
            </TabPanel>

            {/* Tab 2: Keyword Competition */}
            <TabPanel value={tabValue} index={1}>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Keyword</TableCell>
                      <TableCell align="center">Our Position</TableCell>
                      <TableCell align="center">Search Volume</TableCell>
                      <TableCell>Top Competitors</TableCell>
                      <TableCell width={50}></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {keywordCompetition.map((kc) => (
                      <React.Fragment key={kc.keyword}>
                        <TableRow hover>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Typography variant="body2">{kc.keyword}</Typography>
                              <IconButton
                                size="small"
                                onClick={() => copyToClipboard(kc.keyword)}
                                sx={{ ml: 1 }}
                              >
                                <ContentCopyIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              label={kc.ourPosition || 'Not ranking'}
                              color={kc.ourPosition && kc.ourPosition <= 10 ? 'success' : 'default'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell align="center">
                            {kc.searchVolume ? kc.searchVolume.toLocaleString() : '-'}
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                              {kc.competitors.slice(0, 3).map((comp, idx) => {
                                const isOfficial = isOfficialWebsite(comp.domain);
                                return (
                                  <Tooltip 
                                    key={idx} 
                                    title={isOfficial ? "Official website - typically can't be outranked for branded searches" : ""}
                                  >
                                    <Chip
                                      label={`#${comp.position} ${comp.domain}${isOfficial ? ' 🔒' : ''}`}
                                      size="small"
                                      variant="outlined"
                                      color={isOfficial ? 'default' : (comp.position < (kc.ourPosition || 999) ? 'error' : 'default')}
                                      sx={{
                                        backgroundColor: isOfficial ? 'grey.100' : 'transparent',
                                        borderColor: isOfficial ? 'grey.500' : undefined
                                      }}
                                    />
                                  </Tooltip>
                                );
                              })}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <IconButton
                              size="small"
                              onClick={() => toggleKeywordExpansion(kc.keyword)}
                            >
                              {expandedKeywords.has(kc.keyword) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </IconButton>
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell colSpan={5} sx={{ py: 0 }}>
                            <Collapse in={expandedKeywords.has(kc.keyword)} timeout="auto" unmountOnExit>
                              <Box sx={{ p: 2, bgcolor: 'background.default' }}>
                                <Typography variant="subtitle2" gutterBottom>
                                  Competitor Details
                                </Typography>
                                <List dense>
                                  {kc.competitors.map((comp, idx) => {
                                    const isOfficial = isOfficialWebsite(comp.domain);
                                    return (
                                      <ListItem key={idx}>
                                        <ListItemText
                                          primary={
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                              <Chip 
                                                label={`#${comp.position}`} 
                                                size="small" 
                                                sx={{
                                                  backgroundColor: isOfficial ? 'grey.200' : 'default',
                                                  borderColor: isOfficial ? 'grey.500' : undefined
                                                }}
                                              />
                                              <Typography variant="body2">
                                                {comp.domain}
                                                {isOfficial && ' 🔒'}
                                              </Typography>
                                              {isOfficial && (
                                                <Chip 
                                                  label="Official" 
                                                  size="small" 
                                                  sx={{ 
                                                    backgroundColor: 'grey.700', 
                                                    color: 'white',
                                                    height: 20,
                                                    fontSize: '0.65rem'
                                                  }} 
                                                />
                                              )}
                                              <Link
                                                href={comp.url}
                                                target="_blank"
                                                rel="noopener"
                                                sx={{ ml: 'auto' }}
                                              >
                                                <LaunchIcon fontSize="small" />
                                              </Link>
                                            </Box>
                                          }
                                        secondary={
                                          <Box>
                                            <Typography variant="caption" component="div" color="text.primary">
                                              {comp.title}
                                            </Typography>
                                            {comp.snippet && (
                                              <Typography variant="caption" color="text.secondary">
                                                {comp.snippet}
                                              </Typography>
                                            )}
                                          </Box>
                                        }
                                      />
                                    </ListItem>
                                    );
                                  })}
                                </List>
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </TabPanel>

            {/* Tab 3: Detailed Analysis */}
            <TabPanel value={tabValue} index={2}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={8}>
                  <Paper sx={{ p: 3 }}>
                    <Typography variant="h6" gutterBottom>
                      Competitive Landscape Analysis
                    </Typography>
                    <Typography variant="body2" color="text.secondary" paragraph>
                      Based on the analysis of {keywordCompetition.length} keywords where competitors are present:
                    </Typography>
                    
                    <List>
                      <ListItem>
                        <ListItemIcon>
                          <VisibilityIcon />
                        </ListItemIcon>
                        <ListItemText
                          primary="Market Visibility"
                          secondary={`You're competing against ${competitorOverview.length} major competitors across your target keywords`}
                        />
                      </ListItem>
                      
                      <ListItem>
                        <ListItemIcon>
                          <SpeedIcon />
                        </ListItemIcon>
                        <ListItemText
                          primary="Performance Gap"
                          secondary={`Average competitor position: ${
                            competitorOverview.length > 0 
                              ? (competitorOverview.reduce((sum, c) => sum + c.avgPosition, 0) / competitorOverview.length).toFixed(1)
                              : 'N/A'
                          }`}
                        />
                      </ListItem>
                      
                      <ListItem>
                        <ListItemIcon>
                          <TrendingUpIcon />
                        </ListItemIcon>
                        <ListItemText
                          primary="Opportunity Keywords"
                          secondary={`${keywordCompetition.filter(kc => !kc.ourPosition || kc.ourPosition > 10).length} keywords where you're not ranking in top 10`}
                        />
                      </ListItem>
                    </List>
                  </Paper>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Paper sx={{ p: 3 }}>
                    <Typography variant="h6" gutterBottom>
                      Action Items
                    </Typography>
                    <List dense>
                      {insights.map((insight, idx) => (
                        <ListItem key={idx}>
                          <ListItemIcon>
                            {getInsightIcon(insight.type)}
                          </ListItemIcon>
                          <ListItemText
                            primary={insight.title}
                            secondary={
                              <Chip
                                label={insight.priority}
                                color={getPriorityColor(insight.priority) as any}
                                size="small"
                                sx={{ mt: 0.5 }}
                              />
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Paper>
                </Grid>
              </Grid>
            </TabPanel>
          </>
        )}
      </Box>
    </Container>
  );
};

export default CompetitorsPage;